import { useState } from 'react';
import ArticleLink from '@/components/feature/ArticleLink';
import { articlePath } from '@/utils/content';
import type { TutorialGroup } from '@/utils/mt4mt5Tutorials';

type TutorialGroupCardProps = {
  group: TutorialGroup;
  index: number;
  tone: 'primary' | 'accent';
};

const PREVIEW_COUNT = 3;

export default function TutorialGroupCard({ group, index, tone }: TutorialGroupCardProps) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = group.articles.length > PREVIEW_COUNT;
  const visible = expanded ? group.articles : group.articles.slice(0, PREVIEW_COUNT);

  const iconClass = tone === 'primary' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700';
  const numberClass = tone === 'primary' ? 'text-primary-200' : 'text-accent-200';

  return (
    <article className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
      <div className="flex items-start gap-3 border-b border-background-200 bg-background-100/50 px-4 py-3.5 md:px-5">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${iconClass}`}>
          <i className={`${group.icon} text-lg`} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-950 md:text-base">
            {group.label}
          </h3>
          <p className="mt-0.5 text-[11px] text-foreground-500">{group.articles.length} บทความในหมวดนี้</p>
        </div>
        <span className={`font-num text-xl font-semibold leading-none ${numberClass}`}>
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>

      <ul className="flex flex-col divide-y divide-background-200/80">
        {visible.map((article, articleIndex) => (
          <li key={article.slug}>
            <ArticleLink
              to={articlePath(article.slug)}
              className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-primary-50/60 md:px-5"
            >
              <span className="font-num w-5 shrink-0 text-[11px] text-foreground-400">
                {String(articleIndex + 1).padStart(2, '0')}
              </span>
              <span className="min-w-0 flex-1 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                {article.title}
              </span>
              <span className="hidden shrink-0 text-[11px] text-foreground-400 sm:block">{article.readingTime}</span>
              <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-300 transition-colors group-hover:text-primary-600" />
            </ArticleLink>
          </li>
        ))}
      </ul>

      {hasMore && (
        <div className="border-t border-background-200 bg-background-100/30 px-4 py-2.5 md:px-5">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            className="flex w-full cursor-pointer items-center justify-between gap-2 text-xs font-semibold text-primary-700 transition-colors hover:text-primary-900"
          >
            <span>{expanded ? 'ย่อรายการบทความ' : `ดูบทความทั้งหมด (${group.articles.length})`}</span>
            <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`} />
          </button>
        </div>
      )}
    </article>
  );
}